import type { EntityMap } from '@ms/yammer-data-support';
import { moveToFulfilled, moveToPending, moveToRejected, remove, update } from '@ms/yammer-data-support';
import { MessageDeletionOutcome, ThreadDiscoveryHiddenState, ThreadReplySortOrder } from '@ms/yammer-graphql';
import type { Reducer } from 'redux';

import type {
  BestReplyMarkFulfilledAction,
  BestReplyUnmarkFulfilledAction,
} from '../../domains/bestReplyMark/actions.types';
import type { CuratedThreadListLoadAction } from '../../domains/curatedThreadList/actions.types';
import type { DeleteDraftThreadsFulfilledAction } from '../../domains/deleteDraftThreads/actions.types';
import type { DeleteMessageFulfilledAction } from '../../domains/deleteMessage/actions.types';
import type { DeleteThreadFulfilledAction } from '../../domains/deleteThread/actions.types';
import type {
  FeedLoadFulfilledAction,
  FeedLoadNewerThreadsFulfilledAction,
  FeedLoadOlderThreadsFulfilledAction,
} from '../../domains/feedLoad/actions.types';
import type { ThreadMarkAllInboxReadAction } from '../../domains/inboxMarkAllAsRead/actions.types';
import type { PublishModeratedThreadFulfilledAction } from '../../domains/messageWithModerationState/actions.types';
import type { MuteThreadFulfilledAction, UnmuteThreadFulfilledAction } from '../../domains/muteThread/actions.types';
import type {
  NestedThreadDeepLinkSecondLevelLoadAction,
  NestedThreadDeepLinkTopLevelLoadAction,
  NestedThreadLoadAction,
} from '../../domains/nestedThread/types';
import type { NestedThreadListLoadFulfilledAction } from '../../domains/nestedThreadList/actions.types';
import type {
  ThreadPinToEventFeedFulfilledAction,
  ThreadPinToGroupFeedFulfilledAction,
  ThreadPinToTeamsMeetingFeedFulfilledAction,
  ThreadUnpinFromEventFeedFulfilledAction,
  ThreadUnpinFromGroupFeedFulfilledAction,
  ThreadUnpinFromTeamsMeetingFeedFulfilledAction,
} from '../../domains/pinnedThreads/actions.types';
import type {
  PromoteThreadFulfilledAction,
  PromoteThreadRejectedAction,
  UnpromoteThreadFulfilledAction,
  UnpromoteThreadRejectedAction,
} from '../../domains/promoteThread/types';
import type {
  PublishDirectMessageFulfilledAction,
  PublishEditMessageFulfilledAction,
  PublishEventMessageFulfilledAction,
  PublishGroupMessageFulfilledAction,
  PublishNetworkQuestionMessageFulfilledAction,
  PublishPollMessageFulfilledAction,
  PublishPraiseMessageFulfilledAction,
  PublishReplyMessageFulfilledAction,
  PublishTeamsMeetingMessageFulfilledAction,
  PublishUserMessageFulfilledAction,
  PublishUserPollMessageFulfilledAction,
  PublishUserPraiseMessageFulfilledAction,
} from '../../domains/publishers/actions.types';
import type { SearchLoadFulfilledAction, SearchLoadMoreFulfilledAction } from '../../domains/searches/actions.types';
import type {
  DequeueTeamsMeetingThreadFulfilledAction,
  DequeueTeamsMeetingThreadPendingAction,
  DequeueTeamsMeetingThreadRejectedAction,
  QueueTeamsMeetingThreadFulfilledAction,
  QueueTeamsMeetingThreadPendingAction,
  QueueTeamsMeetingThreadRejectedAction,
} from '../../domains/teamsMeetingThreadQueue/actions.types';
import type {
  UpdateThreadAnswerStateFulfilledAction,
  UpdateThreadAnswerStatePendingAction,
  UpdateThreadAnswerStateRejectedAction,
} from '../../domains/threadAnswerState/actions.types';
import type {
  ThreadBookmarkFulfilledAction,
  ThreadBookmarkPendingAction,
  ThreadBookmarkRejectedAction,
  ThreadUnbookmarkFulfilledAction,
  ThreadUnbookmarkPendingAction,
  ThreadUnbookmarkRejectedAction,
} from '../../domains/threadBookmark/types';
import type {
  ThreadCloseAction,
  ThreadCloseFulfilledAction,
  ThreadReopenAction,
} from '../../domains/threadClose/actions.types';
import type { ThreadFollowAction, ThreadUnfollowAction } from '../../domains/threadFollow/actions.types';
import type { ThreadMarkAsSeenFulfilledAction } from '../../domains/threadMarkAsGroupSeenOnServer/actions.types';
import type { ThreadMarkAsQuestionFulfilledAction } from '../../domains/threadMarkAsQuestion/markAsQuestionActions.types';
import type {
  ThreadMarkAsInboxReadAction,
  ThreadMarkAsInboxUnreadAction,
} from '../../domains/threadMarkAsRead/actions.types';
import type { ThreadMoveCompletedAction } from '../../domains/threadMove/actions.types';
import type { RemoveParticipantsFulfilledAction } from '../../domains/threadRemoveParticipants/actions.types';
import type { ThreadSharesLoadAction, ThreadSharesLoadFulfilledAction } from '../../domains/threadShares/actions.types';
import type {
  AddUpvoteThreadFulfilledAction,
  AddUpvoteThreadPendingAction,
  AddUpvoteThreadRejectedAction,
  RemoveUpvoteThreadFulfilledAction,
  RemoveUpvoteThreadPendingAction,
  RemoveUpvoteThreadRejectedAction,
  UpdateUpvoteCountAction,
} from '../../domains/threadUpvote/actions.types';
import { getLargeSortKeyForNewReply } from '../../domains/threads/shared';
import type { ThreadsAppliedTopicNamesLoadFulfilledAction } from '../../domains/threadsAppliedTopicNames/actions.types';
import type {
  AddThreadTopicFulfilledAction,
  RemoveThreadTopicFulfilledAction,
  RemoveTopicFulfilledAction,
} from '../../domains/topics/actions.types';
import type {
  VerifiedReplyMarkFulfilledAction,
  VerifiedReplyUnmarkFulfilledAction,
} from '../../domains/verifiedReplyMark/actions.types';
import type { NormalizedThreadsAppliedTopicNamesEntities } from '../../normalization/types';
import type { ReplyEdge } from '../messages/types';
import type { Topic } from '../types';

import type { Thread, Threads } from './types';

export type ThreadAction =
  | FeedLoadFulfilledAction
  | FeedLoadNewerThreadsFulfilledAction
  | FeedLoadOlderThreadsFulfilledAction
  | ThreadCloseAction
  | ThreadReopenAction
  | ThreadFollowAction
  | ThreadUnfollowAction
  | ThreadMarkAsInboxReadAction
  | ThreadMarkAsInboxUnreadAction
  | ThreadPinToEventFeedFulfilledAction
  | ThreadPinToGroupFeedFulfilledAction
  | ThreadPinToTeamsMeetingFeedFulfilledAction
  | ThreadUnpinFromEventFeedFulfilledAction
  | ThreadUnpinFromGroupFeedFulfilledAction
  | ThreadUnpinFromTeamsMeetingFeedFulfilledAction
  | PublishEventMessageFulfilledAction
  | PublishGroupMessageFulfilledAction
  | PublishUserMessageFulfilledAction
  | PublishUserPollMessageFulfilledAction
  | PublishUserPraiseMessageFulfilledAction
  | PublishDirectMessageFulfilledAction
  | PublishReplyMessageFulfilledAction
  | PublishPollMessageFulfilledAction
  | PublishPraiseMessageFulfilledAction
  | PublishTeamsMeetingMessageFulfilledAction
  | PublishNetworkQuestionMessageFulfilledAction
  | ThreadMarkAsSeenFulfilledAction
  | DeleteMessageFulfilledAction
  | DeleteThreadFulfilledAction
  | DeleteDraftThreadsFulfilledAction
  | BestReplyMarkFulfilledAction
  | BestReplyUnmarkFulfilledAction
  | ThreadMarkAllInboxReadAction
  | SearchLoadFulfilledAction
  | SearchLoadMoreFulfilledAction
  | RemoveParticipantsFulfilledAction
  | ThreadMoveCompletedAction
  | AddThreadTopicFulfilledAction
  | RemoveThreadTopicFulfilledAction
  | PromoteThreadFulfilledAction
  | PromoteThreadRejectedAction
  | UnpromoteThreadFulfilledAction
  | UnpromoteThreadRejectedAction
  | NestedThreadLoadAction
  | NestedThreadDeepLinkTopLevelLoadAction
  | NestedThreadDeepLinkSecondLevelLoadAction
  | NestedThreadListLoadFulfilledAction
  | ThreadsAppliedTopicNamesLoadFulfilledAction
  | ThreadSharesLoadAction
  | ThreadBookmarkFulfilledAction
  | ThreadBookmarkPendingAction
  | ThreadBookmarkRejectedAction
  | ThreadMarkAsQuestionFulfilledAction
  | ThreadUnbookmarkFulfilledAction
  | ThreadUnbookmarkPendingAction
  | ThreadUnbookmarkRejectedAction
  | MuteThreadFulfilledAction
  | UnmuteThreadFulfilledAction
  | AddUpvoteThreadFulfilledAction
  | AddUpvoteThreadPendingAction
  | AddUpvoteThreadRejectedAction
  | RemoveUpvoteThreadFulfilledAction
  | RemoveUpvoteThreadPendingAction
  | RemoveUpvoteThreadRejectedAction
  | UpdateUpvoteCountAction
  | PublishModeratedThreadFulfilledAction
  | CuratedThreadListLoadAction
  | RemoveTopicFulfilledAction
  | PublishEditMessageFulfilledAction
  | QueueTeamsMeetingThreadFulfilledAction
  | QueueTeamsMeetingThreadPendingAction
  | QueueTeamsMeetingThreadRejectedAction
  | DequeueTeamsMeetingThreadPendingAction
  | DequeueTeamsMeetingThreadRejectedAction
  | DequeueTeamsMeetingThreadFulfilledAction
  | UpdateThreadAnswerStateFulfilledAction
  | UpdateThreadAnswerStatePendingAction
  | UpdateThreadAnswerStateRejectedAction
  | VerifiedReplyMarkFulfilledAction
  | VerifiedReplyUnmarkFulfilledAction;

const initialState: Threads = {
  pending: { byId: {} },
  fulfilled: { byId: {} },
  rejected: { byId: {} },
};

const stateWithUpdatedFulfilledThread = (state: EntityMap<Thread>, id: Thread['id'], updatedFields: Partial<Thread>) =>
  update(state, id, updatedFields);

const appendReplyEdgeToThreadOnPublish = (state: Threads, action: PublishReplyMessageFulfilledAction) => {
  const { threadId, messageId, senderId } = action.payload;
  const thread = state.fulfilled.byId[threadId];

  if (!thread || containsReplyEdgeWithNodeId(thread.replyEdges, messageId)) {
    return state;
  }

  // This is an optimistic update of a reply from a publisher,
  // so we will set its publish time to when we append it to the store.
  // The date time string should be padded to match that coming from Graph.
  return stateWithUpdatedFulfilledThread(state, threadId, {
    replyEdges: [
      ...thread.replyEdges,
      {
        nodeId: messageId,
        senderId,
        sortKey: getLargeSortKeyForNewReply(),
        viewerHasSeen: true,
      },
    ],
    repliesCount: thread.repliesCount + 1,
  });
};

const removeParticipantsFromThread = (state: Threads, action: RemoveParticipantsFulfilledAction) => {
  const { threadId, removedUserIds } = action.payload;
  const thread = state.fulfilled.byId[threadId];

  if (!thread) {
    return state;
  }

  const updatedParticipantIds = thread.participantIds.filter((id) => !removedUserIds.includes(id));

  return stateWithUpdatedFulfilledThread(state, threadId, {
    participantIds: updatedParticipantIds,
    participantsCount: updatedParticipantIds.length,
  });
};

const stateWithClearedTeamsMeetingThreads = (state: Threads) => {
  const threads = state.fulfilled.byId;

  const filteredThreads = Object.keys(threads).reduce(
    (filtered: Threads['fulfilled']['byId'], threadId: Thread['id']) => {
      const thread = threads[threadId];
      if (thread.scope.subtype !== 'TeamsMeetingScope') {
        return {
          ...filtered,
          [threadId]: threads[threadId],
        };
      }

      return filtered;
    },
    {}
  );

  return {
    ...state,
    fulfilled: {
      ...state.fulfilled,
      byId: filteredThreads,
    },
  };
};

const removeTopicIdsFromThreads = (state: Threads, topicId: Topic['id']) => {
  const threadIdKeys = Object.keys(state.fulfilled.byId);

  return threadIdKeys.reduce((stateAccum, threadId) => {
    if (!stateAccum.fulfilled.byId[threadId].topicIds.includes(topicId)) {
      return stateAccum;
    }

    const updatedTopics = stateAccum.fulfilled.byId[threadId].topicIds.filter((id) => id !== topicId);

    return stateWithUpdatedFulfilledThread(stateAccum, threadId, { topicIds: updatedTopics });
  }, state);
};

const currentReplyEdgesWithoutLoadedDuplicates = (currentThread: Thread, payloadThread: Thread) =>
  currentThread.replyEdges.filter((edge) => !containsReplyEdgeWithNodeId(payloadThread.replyEdges, edge.nodeId));

const containsReplyEdgeWithNodeId = (replyEdges: ReplyEdge[], nodeId: Thread['id']) =>
  replyEdges.findIndex((edge) => edge.nodeId === nodeId) > -1;

const isAnswerUpvoteQuestionThread = (thread: Thread) =>
  thread.repliesSortedBy === ThreadReplySortOrder.UPVOTE_RANK_THEN_CREATED_AT;

const stateWithNewerRepliesOnFeed = (state: Threads, id: Thread['id'], threads: Record<Thread['id'], Thread>) => {
  const currentThread = state.fulfilled.byId[id];
  const payloadThread = threads[id];

  if (!currentThread) {
    const updatedThread = {
      ...payloadThread,
      replyEdges: isAnswerUpvoteQuestionThread(payloadThread)
        ? [...payloadThread.replyEdges].reverse()
        : [...payloadThread.replyEdges],
    };

    return moveToFulfilled(state, {
      [id]: updatedThread,
    });
  }

  if (isAnswerUpvoteQuestionThread(payloadThread) && payloadThread.replyEdges.length > 0) {
    return state;
  }

  if (payloadThread.replyEdges.length === 0) {
    return moveToFulfilled(state, {
      [id]: payloadThread,
    });
  }

  const currentReplyEdges = currentReplyEdgesWithoutLoadedDuplicates(currentThread, payloadThread);

  const updatedThread = {
    ...payloadThread,
    replyEdges: [...currentReplyEdges, ...payloadThread.replyEdges],
  };

  return stateWithUpdatedFulfilledThread(state, id, updatedThread);
};

const stateWithLoadOrOlderRepliesOnFeed = (state: Threads, id: Thread['id'], threads: Record<Thread['id'], Thread>) => {
  const payloadThread = threads[id];

  if (payloadThread.replyEdges.length === 0) {
    return moveToFulfilled(state, {
      [id]: payloadThread,
    });
  }

  const updatedThread = {
    ...payloadThread,
    replyEdges: isAnswerUpvoteQuestionThread(payloadThread)
      ? [...payloadThread.replyEdges].reverse()
      : [...payloadThread.replyEdges],
  };

  return moveToFulfilled(state, {
    [id]: updatedThread,
  });
};

const markAllThreadsAsReadInInbox = (state: Threads) => {
  const storedThreadIds = Object.keys(state.fulfilled.byId);

  return storedThreadIds.reduce((newState, threadId) => {
    const thread = newState.fulfilled.byId[threadId];
    if (thread.isUnreadInInbox) {
      return stateWithUpdatedFulfilledThread(newState, thread.id, {
        isUnreadInInbox: false,
      });
    }

    return newState;
  }, state);
};

const updateThreadsOnMarkSeen = (state: Threads, action: ThreadMarkAsSeenFulfilledAction) => {
  const threadsToUpdate = action.payload.threadsToMarkAsSeen;
  if (threadsToUpdate.length) {
    return threadsToUpdate.reduce(
      (newState, threadToUpdate) =>
        update(newState, threadToUpdate.threadId, {
          viewerLastSeenMessageId: threadToUpdate.seenMessageId,
        }),
      state
    );
  }

  return state;
};

const stateWithFirstReplyPromoted = (state: Threads, threadId: Thread['id']) => {
  const thread = state.fulfilled.byId[threadId];
  const [firstReply, ...otherReplies] = thread.replyEdges;

  const threadWithNewThreadStarter = {
    ...thread,
    threadStarterId: firstReply.nodeId,
    repliesCount: thread.repliesCount - 1,
    replyEdges: otherReplies,
  };

  return moveToFulfilled(state, { [threadId]: threadWithNewThreadStarter });
};

const stateWithoutUpvoteData = (state: Threads, threadId: Thread['id']) => {
  const thread = state.fulfilled.byId[threadId];
  const updatedThread = {
    ...thread,
    upvoteCount: 0,
    viewerHasUpvoted: false,
  };

  return moveToFulfilled(state, { [threadId]: updatedThread });
};

const removeMessageFromThread = (
  state: Threads,
  { messageId, threadId, deletionOutcome }: DeleteMessageFulfilledAction['payload']
) => {
  if (deletionOutcome === MessageDeletionOutcome.THREAD_REMOVED) {
    return tombstoneThread(state, threadId);
  }

  const thread = state.fulfilled.byId[threadId];
  const isThreadStarter = thread.threadStarterId === messageId;
  const bestReplyId = state.fulfilled.byId[threadId].bestReplyId;
  const isBestReply = bestReplyId === messageId;

  if (isThreadStarter) {
    return deletionOutcome === MessageDeletionOutcome.MESSAGE_REMOVED
      ? stateWithFirstReplyPromoted(state, threadId)
      : stateWithoutUpvoteData(state, threadId);
  }

  const updatedBestReplyId = isBestReply ? undefined : bestReplyId;

  const updatedReplyEdges = thread.replyEdges.filter((edge) => edge.nodeId !== messageId);

  const updatedThread = {
    ...thread,
    bestReplyId: updatedBestReplyId,
    replyEdges: updatedReplyEdges,
    repliesCount: updatedReplyEdges.length,
  };

  return moveToFulfilled(state, { [threadId]: updatedThread });
};

const tombstoneThread = (state: Threads, threadId: Thread['id']) => {
  const updatedFields: Thread = {
    id: threadId,
    telemetryId: '',
    networkId: '',
    databaseId: '',
    groupId: '',
    hasAttachments: false,
    hiddenForNetworkInDiscovery: ThreadDiscoveryHiddenState.NOT_RETRIEVED,
    isDeleted: true,
    repliesCount: 0,
    repliesCountOfUnseen: 0,
    sharesCount: 0,
    threadStarterId: '',
    replyEdges: [],
    hasPreviousPageOfReplies: false,
    participantIds: [],
    participantsCount: 0,
    topicIds: [],
    isFollowing: false,
    createdAt: 0,
    updatedAt: 0,
    isUnreadInInbox: false,
    viewerLastSeenMessageId: '',
    viewerHasSeenThreadStarter: false,
    seenByCount: 0,
    viewerCanBookmark: false,
    viewerCanClose: false,
    viewerCanDelete: false,
    viewerCanHideForNetworkInDiscovery: false,
    viewerCanMarkAsQuestion: false,
    viewerCanMarkBestReply: false,
    viewerCanMarkVerifiedReply: false,
    viewerCanPin: false,
    viewerCanPinInMyLeaders: false,
    viewerCanPromote: false,
    viewerCanMove: false,
    viewerCanReply: false,
    viewerCanPrivateReply: false,
    viewerCanAccessThreadActivity: false,
    viewerCanAddOrRemoveTopic: false,
    isAnnouncement: false,
    isClosed: true,
    isDirectMessage: false,
    isForcedNotification: false,
    promotionEndTime: undefined,
    markSeenKey: '',
    repliesSortedBy: ThreadReplySortOrder.CREATED_AT,
    scope: {
      subtype: 'TombstoneScope',
    },
    viewerHasBookmarked: false,
    viewerHasUpvoted: false,
    upvoteCount: 0,
    replySmallFileUploadUrl: '',
    isVisibleInNetworkQuestionFeeds: false,
  };

  return stateWithUpdatedFulfilledThread(state, threadId, updatedFields);
};

const updateThreadsOnNewThreadsLoad = (state: Threads, newThreads: Record<Thread['id'], Thread> | undefined) => {
  if (!newThreads) {
    return state;
  }

  return Object.keys(newThreads).reduce(
    (newState, threadId) =>
      stateWithNewerRepliesOnFeed(newState, threadId, {
        [threadId]: newThreads[threadId],
      }),
    state
  );
};

const updateThreadsOnThreadsLoad = (state: Threads, newThreads: Record<Thread['id'], Thread> | undefined) => {
  if (!newThreads) {
    return state;
  }

  return Object.keys(newThreads).reduce(
    (newState, threadId) =>
      stateWithLoadOrOlderRepliesOnFeed(newState, threadId, {
        [threadId]: newThreads[threadId],
      }),
    state
  );
};

const addBestReplyToThread = (
  state: Threads,
  { threadId, ...bestReplyFields }: BestReplyMarkFulfilledAction['payload']
) => {
  const thread = state.fulfilled.byId[threadId];

  const threadWithBestReplyAdded = {
    ...thread,
    ...bestReplyFields,
  };

  return stateWithUpdatedFulfilledThread(state, threadId, threadWithBestReplyAdded);
};

const removeBestReplyFromThread = (state: Threads, { threadId }: BestReplyUnmarkFulfilledAction['payload']) => {
  const thread = state.fulfilled.byId[threadId];

  const threadWithBestReplyRemoved = {
    ...thread,
    bestReplyId: undefined,
    bestReplyMarkedByUserId: undefined,
  };

  return stateWithUpdatedFulfilledThread(state, threadId, threadWithBestReplyRemoved);
};

const addVerifiedReplyToThread = (
  state: Threads,
  { threadId, ...verifiedReplyFields }: VerifiedReplyMarkFulfilledAction['payload']
) => {
  const thread = state.fulfilled.byId[threadId];

  const threadWithVerifiedReplyAdded = {
    ...thread,
    ...verifiedReplyFields,
  };

  return stateWithUpdatedFulfilledThread(state, threadId, threadWithVerifiedReplyAdded);
};

const removeVerifiedReplyFromThread = (state: Threads, { threadId }: VerifiedReplyUnmarkFulfilledAction['payload']) => {
  const thread = state.fulfilled.byId[threadId];

  const threadWithVerifiedReplyRemoved = {
    ...thread,
    verifiedReplyId: undefined,
    verifiedReplyMarkedByUserId: undefined,
    verifiedReplyMarkedAt: undefined,
  };

  return stateWithUpdatedFulfilledThread(state, threadId, threadWithVerifiedReplyRemoved);
};

const addThreadTopic = (state: Threads, { threadId, topicId }: AddThreadTopicFulfilledAction['payload']) => {
  const thread = state.fulfilled.byId[threadId];

  if (thread.topicIds.includes(topicId)) {
    return state;
  }

  return stateWithUpdatedFulfilledThread(state, threadId, {
    ...thread,
    topicIds: [...thread.topicIds, topicId],
  });
};

const addThreadShares = (state: Threads, { threadId, result }: ThreadSharesLoadFulfilledAction['payload']) => {
  const thread = state.fulfilled.byId[threadId];
  const shareIds = thread.shareIds || [];

  return stateWithUpdatedFulfilledThread(state, threadId, {
    ...thread,
    shareIds: [...shareIds, ...result.filter((shareId) => !shareIds.includes(shareId))],
  });
};

const removeThreadTopic = (state: Threads, { threadId, topicId }: RemoveThreadTopicFulfilledAction['payload']) => {
  const thread = state.fulfilled.byId[threadId];

  return stateWithUpdatedFulfilledThread(state, threadId, {
    ...thread,
    topicIds: thread.topicIds.filter((id) => id !== topicId),
  });
};

const updateUpvotedThread = (state: Threads, threadId: string) => {
  const thread = state.fulfilled.byId[threadId];

  return stateWithUpdatedFulfilledThread(state, threadId, {
    ...thread,
    viewerHasUpvoted: true,
    upvoteCount: thread.upvoteCount + 1,
  });
};

const updateUnvotedThread = (state: Threads, threadId: string) => {
  const thread = state.fulfilled.byId[threadId];

  return stateWithUpdatedFulfilledThread(state, threadId, {
    ...thread,
    viewerHasUpvoted: false,
    upvoteCount: thread.upvoteCount > 0 ? thread.upvoteCount - 1 : 0,
  });
};

const updateUpvoteCount = (state: Threads, threadId: string, upvoteCount: number) => {
  const thread = state.fulfilled.byId[threadId];

  return stateWithUpdatedFulfilledThread(state, threadId, {
    ...thread,
    upvoteCount,
  });
};

const updateThreadState = (state: Threads, payload: PublishEditMessageFulfilledAction['payload']) => {
  const { threadId, isAnnouncement, isForcedNotification, viewerCanMarkBestReply, viewerCanMarkVerifiedReply } =
    payload;
  const thread = state.fulfilled.byId[threadId];

  if (isAnnouncement === undefined) {
    return stateWithUpdatedFulfilledThread(state, threadId, {
      ...thread,
      viewerCanMarkBestReply,
      viewerCanMarkVerifiedReply,
    });
  }

  return stateWithUpdatedFulfilledThread(state, threadId, {
    ...thread,
    isAnnouncement,
    isForcedNotification,
    viewerCanMarkBestReply,
    viewerCanMarkVerifiedReply,
  });
};

const updateThreadViewerCanMarkState = (state: Threads, payload: ThreadMarkAsQuestionFulfilledAction['payload']) => {
  const { threadId, viewerCanMarkBestReply, viewerCanMarkVerifiedReply } = payload;
  const thread = state.fulfilled.byId[threadId];

  return stateWithUpdatedFulfilledThread(state, threadId, {
    ...thread,
    viewerCanMarkBestReply,
    viewerCanMarkVerifiedReply,
  });
};

const closeThread = (state: Threads, { id, viewerCanClose }: ThreadCloseFulfilledAction['payload']) => {
  const thread = state.fulfilled.byId[id];

  if (viewerCanClose !== undefined) {
    return stateWithUpdatedFulfilledThread(state, id, {
      ...thread,
      viewerCanClose,
      isClosed: true,
    });
  }

  return stateWithUpdatedFulfilledThread(state, id, {
    ...thread,
    isClosed: true,
  });
};

const updateTopicIdsInThreads = (state: Threads, threads: NormalizedThreadsAppliedTopicNamesEntities['threads']) => {
  if (!threads) {
    return state;
  }

  const threadIdKeys = Object.keys(threads);

  return threadIdKeys.reduce(
    (stateAccum, threadId) => update(stateAccum, threadId, { topicIds: threads[threadId].topics }),
    state
  );
};

// eslint-disable-next-line max-lines-per-function
const reducer: Reducer<Threads, ThreadAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'THREAD.PIN_TO_EVENT_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.threads);
    case 'THREAD.PIN_TO_GROUP_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.threads);
    case 'THREAD.PIN_TO_TEAMS_MEETING_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.threads);
    case 'THREAD.UNPIN_FROM_EVENT_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.threads);
    case 'THREAD.UNPIN_FROM_GROUP_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.threads);
    case 'THREAD.UNPIN_FROM_TEAMS_MEETING_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.threads);
    case 'SEARCH.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.threads);
    case 'SEARCH.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.threads);
    case 'FEED.LOAD_FULFILLED': {
      let previousState = state;
      if (action.payload.clearExistingThreads) {
        previousState = stateWithClearedTeamsMeetingThreads(previousState);
      }

      return updateThreadsOnThreadsLoad(previousState, action.payload.entities.threads);
    }
    case 'FEED.LOAD_NEWER_THREADS_FULFILLED':
      return updateThreadsOnNewThreadsLoad(state, action.payload.entities.threads);
    case 'FEED.LOAD_OLDER_THREADS_FULFILLED':
      return updateThreadsOnThreadsLoad(state, action.payload.entities.threads);
    case 'PUBLISHER.PUBLISH_EVENT_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.threads);
    case 'PUBLISHER.PUBLISH_GROUP_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.threads);
    case 'PUBLISHER.PUBLISH_USER_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.threads);
    case 'PUBLISHER.PUBLISH_USER_POLL_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.threads);
    case 'PUBLISHER.PUBLISH_USER_PRAISE_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.threads);
    case 'PUBLISHER.PUBLISH_DIRECT_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.threads);
    case 'PUBLISHER.PUBLISH_REPLY_MESSAGE_FULFILLED':
      return appendReplyEdgeToThreadOnPublish(state, action);
    case 'PUBLISHER.PUBLISH_POLL_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.threads);
    case 'PUBLISHER.PUBLISH_PRAISE_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.threads);
    case 'PUBLISHER.PUBLISH_TEAMS_MEETING_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.threads);
    case 'PUBLISHER.PUBLISH_NETWORK_QUESTION_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.threads);
    case 'PUBLISHER.PUBLISH_EDIT_MESSAGE_FULFILLED':
      return updateThreadState(state, action.payload);
    case 'THREAD.MARK_AS_QUESTION_FULFILLED':
      return updateThreadViewerCanMarkState(state, action.payload);
    case 'THREAD.UNFOLLOW_REJECTED':
      return stateWithUpdatedFulfilledThread(state, action.payload.id, { isFollowing: true });
    case 'THREAD.FOLLOW_FULFILLED':
      return stateWithUpdatedFulfilledThread(state, action.payload.id, { isFollowing: true });
    case 'THREAD.FOLLOW_PENDING':
      return stateWithUpdatedFulfilledThread(state, action.payload.id, { isFollowing: true });
    case 'THREAD.FOLLOW_REALTIME':
      return stateWithUpdatedFulfilledThread(state, action.payload.id, { isFollowing: true });
    case 'THREAD.FOLLOW_REJECTED':
      return stateWithUpdatedFulfilledThread(state, action.payload.id, { isFollowing: false });
    case 'THREAD.UNFOLLOW_FULFILLED':
      return stateWithUpdatedFulfilledThread(state, action.payload.id, { isFollowing: false });
    case 'THREAD.UNFOLLOW_PENDING':
      return stateWithUpdatedFulfilledThread(state, action.payload.id, { isFollowing: false });
    case 'THREAD.UNFOLLOW_REALTIME':
      return stateWithUpdatedFulfilledThread(state, action.payload.id, { isFollowing: false });
    case 'THREAD.QUEUE_PENDING':
      return stateWithUpdatedFulfilledThread(state, action.payload.id, { isQueued: true });
    case 'THREAD.QUEUE_FULFILLED':
      return stateWithUpdatedFulfilledThread(state, action.payload.id, { isQueued: true });
    case 'THREAD.QUEUE_REJECTED':
      return stateWithUpdatedFulfilledThread(state, action.payload.id, { isQueued: false });
    case 'THREAD.DEQUEUE_PENDING':
      return stateWithUpdatedFulfilledThread(state, action.payload.id, { isQueued: false });
    case 'THREAD.DEQUEUE_FULFILLED':
      return stateWithUpdatedFulfilledThread(state, action.payload.id, { isQueued: false });
    case 'THREAD.DEQUEUE_REJECTED':
      return stateWithUpdatedFulfilledThread(state, action.payload.id, { isQueued: true });
    case 'THREAD.UPDATE_ANSWER_STATE_PENDING':
    case 'THREAD.UPDATE_ANSWER_STATE_FULFILLED':
    case 'THREAD.UPDATE_ANSWER_STATE_REJECTED':
      return stateWithUpdatedFulfilledThread(state, action.payload.id, {
        answerState: action.payload.answerState,
      });
    case 'THREAD.MARK_AS_INBOX_UNREAD_REJECTED':
      return stateWithUpdatedFulfilledThread(state, action.payload.id, { isUnreadInInbox: false });
    case 'THREAD.MARK_AS_INBOX_READ_FULFILLED':
      return stateWithUpdatedFulfilledThread(state, action.payload.threadEdge.nodeId, { isUnreadInInbox: false });
    case 'THREAD.MARK_AS_INBOX_READ_PENDING':
      return stateWithUpdatedFulfilledThread(state, action.payload.id, { isUnreadInInbox: false });
    case 'THREAD.MARK_AS_INBOX_READ_REALTIME':
      return stateWithUpdatedFulfilledThread(state, action.payload.threadEdge.nodeId, { isUnreadInInbox: false });
    case 'THREAD.MARK_AS_INBOX_READ_REJECTED':
      return stateWithUpdatedFulfilledThread(state, action.payload.id, { isUnreadInInbox: true });
    case 'THREAD.MARK_AS_INBOX_UNREAD_FULFILLED':
      return stateWithUpdatedFulfilledThread(state, action.payload.threadEdge.nodeId, { isUnreadInInbox: true });
    case 'THREAD.MARK_AS_INBOX_UNREAD_PENDING':
      return stateWithUpdatedFulfilledThread(state, action.payload.id, { isUnreadInInbox: true });
    case 'THREAD.MARK_AS_INBOX_UNREAD_REALTIME':
      return stateWithUpdatedFulfilledThread(state, action.payload.threadEdge.nodeId, { isUnreadInInbox: true });
    case 'THREAD.MARK_ALL_AS_INBOX_READ_REALTIME':
      return markAllThreadsAsReadInInbox(state);
    case 'THREAD.MARK_AS_GROUP_SEEN_FULFILLED':
      return updateThreadsOnMarkSeen(state, action);
    case 'MESSAGE.DELETE_FULFILLED':
      return removeMessageFromThread(state, action.payload);
    case 'BEST_REPLY.MARK_FULFILLED':
      return addBestReplyToThread(state, action.payload);
    case 'BEST_REPLY.UNMARK_FULFILLED':
      return removeBestReplyFromThread(state, action.payload);
    case 'THREAD.DELETE_FULFILLED':
      return tombstoneThread(state, action.payload.threadId);
    case 'THREADS.DELETE_FULFILLED':
      return remove(state, action.payload.threadIds);
    case 'THREAD.MARK_ALL_INBOX_READ_FULFILLED':
      return markAllThreadsAsReadInInbox(state);
    case 'THREAD.CLOSE_FULFILLED':
      return closeThread(state, action.payload);
    case 'THREAD.REOPEN_FULFILLED':
      return stateWithUpdatedFulfilledThread(state, action.payload.id, { isClosed: false });
    case 'THREAD.REMOVE_PARTICIPANTS_FULFILLED':
      return removeParticipantsFromThread(state, action);
    case 'THREAD.MOVE_COMPLETED':
      return stateWithUpdatedFulfilledThread(state, action.payload.threadId, { groupId: action.payload.groupId });
    case 'THREAD_TOPIC.ADD_FULFILLED':
      return addThreadTopic(state, action.payload);
    case 'THREAD_TOPIC.REMOVE_FULFILLED':
      return removeThreadTopic(state, action.payload);
    case 'THREAD.PROMOTE_REJECTED':
      return moveToRejected(state, [action.payload.threadId], action.payload.error);
    case 'THREAD.PROMOTE_FULFILLED':
      return stateWithUpdatedFulfilledThread(state, action.payload.threadId, {
        promotionEndTime: action.payload.promotionEndTime,
      });
    case 'THREAD.UNPROMOTE_REJECTED':
      return moveToRejected(state, [action.payload.threadId], action.payload.error);
    case 'THREAD.UNPROMOTE_FULFILLED':
      return stateWithUpdatedFulfilledThread(state, action.payload.threadId, {
        promotionEndTime: undefined,
      });

    case 'NESTED_THREAD.LOAD_PENDING':
      return moveToPending(state, [action.payload.threadId]);
    case 'NESTED_THREAD.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.threads);
    case 'NESTED_THREAD.LOAD_REJECTED':
      return moveToRejected(state, [action.payload.threadId], action.payload.error);

    case 'NESTED_THREAD.LOAD_DEEP_LINK_TOP_LEVEL_PENDING':
      return moveToPending(state, [action.payload.threadId]);
    case 'NESTED_THREAD.LOAD_DEEP_LINK_TOP_LEVEL_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.threads);
    case 'NESTED_THREAD.LOAD_DEEP_LINK_TOP_LEVEL_REJECTED':
      return moveToRejected(state, [action.payload.threadId], action.payload.error);

    case 'NESTED_THREAD.LOAD_DEEP_LINK_SECOND_LEVEL_PENDING':
      return moveToPending(state, [action.payload.threadId]);
    case 'NESTED_THREAD.LOAD_DEEP_LINK_SECOND_LEVEL_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.threads);
    case 'NESTED_THREAD.LOAD_DEEP_LINK_SECOND_LEVEL_REJECTED':
      return moveToRejected(state, [action.payload.threadId], action.payload.error);

    case 'NESTED_THREAD_LIST.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.threads);

    case 'THREAD_SHARES.LOAD_FULFILLED':
      return addThreadShares(state, action.payload);

    case 'THREAD.BOOKMARK_PENDING':
      return stateWithUpdatedFulfilledThread(state, action.payload.threadId, { viewerHasBookmarked: false });
    case 'THREAD.BOOKMARK_FULFILLED':
      return stateWithUpdatedFulfilledThread(state, action.payload.threadId, { viewerHasBookmarked: true });
    case 'THREAD.BOOKMARK_REJECTED':
      return stateWithUpdatedFulfilledThread(state, action.payload.threadId, { viewerHasBookmarked: false });

    case 'THREAD.UNBOOKMARK_PENDING':
      return stateWithUpdatedFulfilledThread(state, action.payload.threadId, { viewerHasBookmarked: true });
    case 'THREAD.UNBOOKMARK_FULFILLED':
      return stateWithUpdatedFulfilledThread(state, action.payload.threadId, { viewerHasBookmarked: false });
    case 'THREAD.UNBOOKMARK_REJECTED':
      return stateWithUpdatedFulfilledThread(state, action.payload.threadId, { viewerHasBookmarked: true });

    case 'THREAD.MUTE_FULFILLED':
      return stateWithUpdatedFulfilledThread(state, action.payload.threadId, {
        hiddenForNetworkInDiscovery: ThreadDiscoveryHiddenState.HIDDEN,
      });
    case 'THREAD.UNMUTE_FULFILLED':
      return stateWithUpdatedFulfilledThread(state, action.payload.threadId, {
        hiddenForNetworkInDiscovery: ThreadDiscoveryHiddenState.NOT_HIDDEN,
      });

    case 'THREAD.ADD_UPVOTE_PENDING':
      return updateUpvotedThread(state, action.payload.threadId);
    case 'THREAD.REMOVE_UPVOTE_PENDING':
      return updateUnvotedThread(state, action.payload.threadId);

    case 'THREAD.UPDATE_UPVOTE_COUNT':
      return updateUpvoteCount(state, action.payload.threadId, action.payload.upvoteCount);
    case 'PUBLISH_MODERATED_THREAD.FULFILLED':
      return stateWithUpdatedFulfilledThread(state, action.payload.threadId, { viewerCanReply: true });
    case 'CURATED_THREAD_LIST_LOAD.FULFILLED':
      return moveToFulfilled(state, action.payload.entities.threads);
    case 'TOPIC.REMOVE_FULFILLED':
      return removeTopicIdsFromThreads(state, action.payload.topicId);
    case 'THREADS_APPLIED_TOPIC_NAMES.LOAD_FULFILLED':
      return updateTopicIdsInThreads(state, action.payload.entities.threads);
    case 'VERIFIED_REPLY.MARK_FULFILLED':
      return addVerifiedReplyToThread(state, action.payload);
    case 'VERIFIED_REPLY.UNMARK_FULFILLED':
      return removeVerifiedReplyFromThread(state, action.payload);
    default:
      return state;
  }
};

export default reducer;
